/**
 * NexusReferralDetail - Referral detail view with workflow actions
 * Triage, Match, Route, Place, Notes, Audit trail
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft, Zap, Route, CheckCircle2, Clock, AlertTriangle,
  Send, Loader2, User, FileText, Target, StickyNote, TrendingUp
} from "lucide-react";
import NexusMatchConsole from './NexusMatchConsole';
import NexusAIRecommend from './NexusAIRecommend';

const STATUS_COLORS = {
  intake: "bg-blue-100 text-blue-700",
  triaged: "bg-amber-100 text-amber-700",
  matching: "bg-purple-100 text-purple-700",
  routed: "bg-cyan-100 text-cyan-700",
  accepted: "bg-emerald-100 text-emerald-700",
  placed: "bg-green-100 text-green-800",
  declined: "bg-red-100 text-red-700",
  closed: "bg-slate-100 text-slate-600",
};

const NexusReferralDetail = ({ referralId, token, API, user, onBack }) => {
  const [referral, setReferral] = useState(null);
  const [client, setClient] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matchResults, setMatchResults] = useState(null);
  const [matching, setMatching] = useState(false);
  const [triaging, setTriaging] = useState(false);
  const [routing, setRouting] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [triageForm, setTriageForm] = useState({ urgency: "", risk_level: "" });
  const [selectedClinicians, setSelectedClinicians] = useState([]);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/nexus/referrals/${referralId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setReferral(data.data);
        setClient(data.client);
        setEvents(data.events || []);
        setTriageForm({
          urgency: data.data.urgency || "routine",
          risk_level: data.data.risk_level || "low",
        });
      }
    } catch (err) {
      toast.error("Failed to load referral");
    } finally {
      setLoading(false);
    }
  }, [API, token, referralId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleTriage = async () => {
    setTriaging(true);
    try {
      const res = await fetch(`${API}/api/nexus/referrals/${referralId}/triage`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(triageForm),
      });
      const data = await res.json();
      if (data.success) { toast.success("Triage complete"); fetchDetail(); }
      else toast.error(data.detail || "Triage failed");
    } catch (err) { toast.error("Network error"); }
    finally { setTriaging(false); }
  };

  const handleMatch = async () => {
    setMatching(true);
    try {
      const res = await fetch(`${API}/api/nexus/referrals/${referralId}/match`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setMatchResults(data);
        toast.success(`Matched ${data.summary?.passed_hard_stops || 0} clinicians`);
        fetchDetail();
      } else toast.error(data.detail || "Matching failed");
    } catch (err) { toast.error("Network error"); }
    finally { setMatching(false); }
  };

  const handleRoute = async () => {
    if (selectedClinicians.length === 0) {
      toast.error("Select at least one clinician to route");
      return;
    }
    setRouting(true);
    try {
      const res = await fetch(`${API}/api/nexus/referrals/${referralId}/route`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ clinician_ids: selectedClinicians }),
      });
      const data = await res.json();
      if (data.success) { toast.success("Routed successfully"); fetchDetail(); setSelectedClinicians([]); }
      else toast.error(data.detail || "Routing failed");
    } catch (err) { toast.error("Network error"); }
    finally { setRouting(false); }
  };

  const handlePlace = async (clinicianId) => {
    setPlacing(true);
    try {
      const res = await fetch(`${API}/api/nexus/referrals/${referralId}/place`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ clinician_id: clinicianId }),
      });
      const data = await res.json();
      if (data.success) { toast.success("Placement confirmed!"); fetchDetail(); }
      else toast.error(data.detail || "Placement failed");
    } catch (err) { toast.error("Network error"); }
    finally { setPlacing(false); }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      const res = await fetch(`${API}/api/nexus/referrals/${referralId}/notes`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ text: noteText }),
      });
      const data = await res.json();
      if (data.success) { toast.success("Note added"); setNoteText(""); fetchDetail(); }
    } catch (err) { toast.error("Network error"); }
    finally { setAddingNote(false); }
  };

  const toggleClinician = (id) => {
    setSelectedClinicians(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>;
  }
  if (!referral) return null;

  const statusColor = STATUS_COLORS[referral.status] || "bg-slate-100 text-slate-600";

  return (
    <div data-testid="nexus-referral-detail" className="space-y-4">
      {/* Back + Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="referral-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-slate-900">
              {referral.client_first_name} {referral.client_last_name}
            </h2>
            <Badge className={`${statusColor} border-0 text-xs`}>{referral.status?.toUpperCase()}</Badge>
          </div>
          <p className="text-xs text-slate-500">
            {referral.service_requested || "—"} &middot; {referral.urgency} &middot; Created {new Date(referral.created_at).toLocaleString()}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Info */}
        <div className="lg:col-span-2 space-y-4">
          {/* Demographics */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><User className="w-4 h-4 text-indigo-600" /> Client Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div><span className="text-slate-500 text-xs">DOB</span><p>{referral.client_dob || "—"}</p></div>
                <div><span className="text-slate-500 text-xs">Gender</span><p>{referral.client_gender || "—"}</p></div>
                <div><span className="text-slate-500 text-xs">County</span><p>{referral.client_county || "—"}</p></div>
                <div><span className="text-slate-500 text-xs">Phone</span><p>{referral.client_phone || "—"}</p></div>
                <div><span className="text-slate-500 text-xs">Email</span><p>{referral.client_email || "—"}</p></div>
                <div><span className="text-slate-500 text-xs">Medicaid</span><p>{referral.client_medicaid_id || "—"}</p></div>
                <div><span className="text-slate-500 text-xs">Guardian</span><p>{referral.client_guardian_name || "—"}</p></div>
                <div><span className="text-slate-500 text-xs">Guardian Phone</span><p>{referral.client_guardian_phone || "—"}</p></div>
                <div><span className="text-slate-500 text-xs">Payer</span><p>{referral.payer_name || referral.payer_type || "—"}</p></div>
              </div>
            </CardContent>
          </Card>

          {/* Clinical */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-indigo-600" /> Clinical Need</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <p>{referral.presenting_need || "—"}</p>
              <div className="flex flex-wrap gap-2">
                {(referral.diagnosis_codes || []).map((dx, i) => (
                  <Badge key={i} variant="outline" className="text-xs">{dx}</Badge>
                ))}
              </div>
              <div className="flex gap-4 text-xs text-slate-500">
                <span>Service: {referral.service_requested || "—"}</span>
                <span>Modality: {referral.preferred_modality || "—"}</span>
                <span>Language: {referral.language_preference || "—"}</span>
                <span>Source: {referral.source_type} - {referral.source_name || "—"}</span>
              </div>
            </CardContent>
          </Card>

          {/* Match Console (Phase 2 — Explainable scores) */}
          {matchResults && matchResults.data && (
            <NexusMatchConsole
              matchResults={matchResults}
              selectedClinicians={selectedClinicians}
              onToggleClinician={toggleClinician}
              onRoute={handleRoute}
              routing={routing}
              referralStatus={referral.status}
            />
          )}

          {/* Sage AI Recommendation */}
          {["intake", "triaged", "matching", "routed"].includes(referral.status) && (
            <NexusAIRecommend
              referralId={referralId}
              token={token}
              API={API}
              onRoute={fetchDetail}
              referralStatus={referral.status}
            />
          )}

          {/* Routed Clinicians */}
          {referral.routed_to && referral.routed_to.length > 0 && (
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Route className="w-4 h-4 text-cyan-600" /> Routed Clinicians
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {referral.routed_to.map((r, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded border border-slate-200">
                      <div>
                        <p className="text-sm font-medium">{r.clinician_id}</p>
                        <p className="text-xs text-slate-500">Routed {new Date(r.routed_at).toLocaleString()}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {r.response ? (
                          <Badge className={r.response === "accepted" ? "bg-emerald-100 text-emerald-700 border-0" : "bg-red-100 text-red-700 border-0"}>
                            {r.response}
                          </Badge>
                        ) : (
                          <>
                            <Badge variant="outline" className="text-xs">Pending</Badge>
                            {referral.status !== "placed" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs h-7"
                                onClick={() => handlePlace(r.clinician_id)}
                                disabled={placing}
                                data-testid={`place-btn-${r.clinician_id}`}
                              >
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Place
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: Actions + Notes + Events */}
        <div className="space-y-4">
          {/* Actions */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Triage */}
              {(referral.status === "intake" || referral.status === "triaged") && (
                <div className="space-y-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-xs font-medium text-amber-800">Triage</p>
                  <Select value={triageForm.urgency} onValueChange={(v) => setTriageForm(p => ({ ...p, urgency: v }))}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Urgency" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="routine">Routine</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="emergent">Emergent</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={triageForm.risk_level} onValueChange={(v) => setTriageForm(p => ({ ...p, risk_level: v }))}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Risk" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="moderate">Moderate</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" className="w-full h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white" onClick={handleTriage} disabled={triaging} data-testid="triage-btn">
                    {triaging ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
                    Set Triage
                  </Button>
                </div>
              )}

              {/* Match */}
              {["intake", "triaged", "matching"].includes(referral.status) && (
                <Button
                  size="sm"
                  className="w-full h-8 text-xs bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={handleMatch}
                  disabled={matching}
                  data-testid="run-match-btn"
                >
                  {matching ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
                  Run Matching Engine
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <StickyNote className="w-4 h-4 text-indigo-600" /> Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex gap-2">
                <Textarea
                  data-testid="referral-note-input"
                  placeholder="Add a note..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  rows={2}
                  className="text-xs"
                />
                <Button size="sm" variant="outline" className="shrink-0" onClick={handleAddNote} disabled={addingNote} data-testid="add-note-btn">
                  <Send className="w-3 h-3" />
                </Button>
              </div>
              {(referral.notes || []).map((n, i) => (
                <div key={i} className="text-xs p-2 bg-slate-50 rounded border border-slate-100">
                  <p className="text-slate-700">{n.text}</p>
                  <p className="text-slate-400 mt-1">{n.author_name} &middot; {new Date(n.timestamp).toLocaleString()}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Audit Trail */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-500" /> Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1.5">
                {events.map((ev, i) => (
                  <div key={i} className="text-xs flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-1.5 shrink-0" />
                    <div>
                      <span className="font-medium text-slate-700">{ev.event_type}</span>
                      {ev.from_status && ev.to_status && (
                        <span className="text-slate-400"> ({ev.from_status} &rarr; {ev.to_status})</span>
                      )}
                      <p className="text-slate-400">{ev.actor_name} &middot; {new Date(ev.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
                {events.length === 0 && <p className="text-xs text-slate-400">No activity yet</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default NexusReferralDetail;
